import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { AccountDetailsFlowType, SelectFunctionType } from '../enum';
import { AccountPasswordResetRequest, ChangePassword, CredentialsModel, PersonalDetails, TokenResult } from '../models';
import { AccountCreate } from '../models/account.model';
import {
  authenticateAction,
  createAccountAction,
  getAccountPropertiesAction,
  getEmailDomainsAction,
  goHomeAction,
  goHomeSuccessAction,
  loadAccountDetailsAction,
  logoutAction,
  logoutSuccessAction,
  personalDetailsAction,
  changePasswordAction,
  sendResetPasswordEmailAction,
  sendValidationEmailAction,
  setTokenAndRefreshTokenAction,
  softCheckAuthenticationAction,
  deactivateAccountAction,
  AccountResetPasswordAction,
  setIsLoadingAction,
  clearCartAfterFiveHrsAction,
} from './authentication.actions';
import { IAuthenticationFeature } from './authentication.feature.state';
import {
  deactivateAccount,
  getAccountDetails,
  getAccountProperties,
  getAuthToken,
  getChangePasswordResponse,
  getEmail,
  getEmailDomains,
  getFailures,
  getIsLoading,
  getLoginState,
  getPersonalDetailsResponse,
  getSoftCheckResponse,
} from './authentication.selectors';

@Injectable()
export class AuthenticationFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public failures$ = this.store.select(getFailures);
  public accountDetails$ = this.store.select(getAccountDetails);
  public softCheckResponse$ = this.store.select(getSoftCheckResponse);
  public email$ = this.store.select(getEmail);
  public getAccountProperties$ = this.store.select(getAccountProperties);
  public getpersonalDetailsResponse$ = this.store.select(getPersonalDetailsResponse);
  public getchangePasswordResponse$ = this.store.select(getChangePasswordResponse);
  public getEmailDomains$ = this.store.select(getEmailDomains);
  public deactivateAccountResponse$ = this.store.select(deactivateAccount);
  public isAuthenticated$ = this.store.select(getAuthToken).pipe(
    map((tokenResult) => {
      return tokenResult !== null;
    })
  );

  public isAccountValidated$ = this.store.select(getAccountDetails).pipe(
    map((accountDetails) => {
      return !!accountDetails?.validated;
    })
  );

  public isAuthenticated = new BehaviorSubject<boolean>(false);
  public goHomebuttonPressed = new BehaviorSubject<boolean>(false);
  public isAccountValidated = new BehaviorSubject<boolean>(false);
  public autoValidation = null;

  public userName$ = this.accountDetails$.pipe(
    map((accountDetails) => {
      return accountDetails ? `${accountDetails.firstName} ${accountDetails.surname}` : '';
    })
  );
  public loyaltyPoints$ = this.accountDetails$.pipe(
    map((accountDetails) => {
      return accountDetails?.loyaltyPoints ? accountDetails.loyaltyPoints : 0;
    })
  );
  public loyaltyPointsName$ = this.accountDetails$.pipe(
    map((accountDetails) => {
      return accountDetails?.loyaltyPointsName ? accountDetails.loyaltyPointsName : 'Loyalty points';
    })
  );
  public qrCodeData$ = this.accountDetails$.pipe(
    map((accountDetails) => {
      return accountDetails?.qrCode ? accountDetails.qrCode : null;
    })
  );
  public getFunctionsPermissions$: Observable<SelectFunctionType[]> = this.accountDetails$.pipe(
    map((accountDetails) => {
      const allowedFunctions: SelectFunctionType[] = [];
      if (accountDetails?.clickCollectAllowed) allowedFunctions.push(SelectFunctionType.ClickAndCollect);
      if (accountDetails?.scanAndGoAllowed) allowedFunctions.push(SelectFunctionType.ScanAndGo);
      if (accountDetails?.loyaltyAllowed) allowedFunctions.push(SelectFunctionType.Loyalty);
      return allowedFunctions;
    })
  );

  public resetPasswordRequest = new BehaviorSubject<boolean>(false);
  public accountResetPasswordRequest = new BehaviorSubject<boolean>(false);
  private tokenForValidation = new BehaviorSubject<string>('');

  constructor(private store: Store<IAuthenticationFeature>) {}

  public getToken(): Observable<TokenResult> {
    return this.store.select(getLoginState).pipe(
      map((state) => {
        return state.locationToken ? state.locationToken : state.authToken;
      })
    );
  }

  public getAuthenticationToken(): Observable<TokenResult> {
    return this.store.select(getAuthToken).pipe(
      map((tokenResult) => {
        this.isAuthenticated.next(!!tokenResult);
        return tokenResult;
      })
    );
  }

  public getLocationToken(): Observable<TokenResult> {
    return this.store.select(getAuthToken).pipe(
      map((tokenResult) => tokenResult),
      first()
    );
  }

  public authenticate(credentials: CredentialsModel): void {
    this.store.dispatch(authenticateAction({ credentials }));
  }

  public setTokenInformation(token: TokenResult): void {
    this.store.dispatch(setTokenAndRefreshTokenAction({ tokenInfo: token }));
  }

  public logout(): void {
    this.isAuthenticated.next(false);
    this.store.dispatch(logoutAction());
  }

  public logoutSuccess(withNavigation = true): void {
    this.store.dispatch(logoutSuccessAction({ withNavigation }));
  }

  public goHome(): void {
    this.store.dispatch(goHomeAction());
  }

  public goHomeSuccess(): void {
    this.store.dispatch(goHomeSuccessAction());
  }

  public loadAccountDetails(accountDetailsFlowType: AccountDetailsFlowType): void {
    this.store.dispatch(loadAccountDetailsAction({ accountDetailsFlowType }));
  }

  public softCheck(): void {
    this.store.dispatch(softCheckAuthenticationAction());
  }

  public sendValidationEmail(): void {
    this.store.dispatch(sendValidationEmailAction());
  }

  public sendResetPasswordEmail(email: string): void {
    this.store.dispatch(sendResetPasswordEmailAction({ email }));
  }

  public sendAccountResetPasswordRequest(payload: AccountPasswordResetRequest): void {
    this.store.dispatch(AccountResetPasswordAction({ payload }));
  }

  public createAccount(createAccount: AccountCreate): void {
    this.store.dispatch(createAccountAction({ createAccount }));
  }

  public updatepersonalDetails(personalDetails: PersonalDetails): void {
    this.store.dispatch(personalDetailsAction({ personalDetails }));
  }

  public updateChangePassword(changePassword: ChangePassword): void {
    this.store.dispatch(changePasswordAction({ changePassword }));
  }

  public getAccountProperties(): void {
    this.store.dispatch(getAccountPropertiesAction());
  }

  public getEmailDomains(): void {
    this.store.dispatch(getEmailDomainsAction());
  }

  public deactivateAccount(): void {
    this.store.dispatch(deactivateAccountAction());
  }

  public setIsLoading(isLoading: boolean): void {
    this.store.dispatch(setIsLoadingAction({ isLoading }));
  }

  public setAutoValidation(value: number) {
    this.autoValidation = value;
  }

  public setTokenForValidation(token: string) {
    this.tokenForValidation.next(token);
  }
  public getTokenForValidation() {
    return this.tokenForValidation.value;
  }

  public setResetPasswordRequest(requested: boolean) {
    this.resetPasswordRequest.next(requested);
  }
  public getResetPasswordRequest() {
    return this.resetPasswordRequest.value;
  }

  public setAccountResetPasswordRequest(requested: boolean) {
    this.accountResetPasswordRequest.next(requested);
  }

  public getAccountResetPasswordRequest() {
    return this.accountResetPasswordRequest.value;
  }

  public clearCartAfterFiveHrsCheck(): void {
    this.store.dispatch(clearCartAfterFiveHrsAction());
  }
}
